/**
 * Time Trial Assessment Component
 *
 * Read-only display of a rider's time-trial fitness zone within the Skills Panel.
 * Shows zone classification with color-coded indicator, formatted times,
 * group rider count, and cross-group comparison note when applicable.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8, 3.9
 */

import type { TimeTrialZoneResponse } from '../../../types/skills';
import { getZoneColor, formatTimeTrialTime } from '../../../utils/timeTrialUtils';

interface TimeTrialAssessmentProps {
  data: TimeTrialZoneResponse;
}

/**
 * Map zone color string to Tailwind CSS classes for the indicator dot and container.
 */
function getZoneClasses(color: string): { dot: string; container: string } {
  switch (color) {
    case 'orange':
      return { dot: 'bg-orange-500', container: 'bg-orange-50 border-orange-200' };
    case 'blue':
      return { dot: 'bg-blue-500', container: 'bg-blue-50 border-blue-200' };
    case 'green':
      return { dot: 'bg-green-500', container: 'bg-green-50 border-green-200' };
    case 'dark green':
      return { dot: 'bg-green-700', container: 'bg-green-50 border-green-300' };
    case 'gray':
    default:
      return { dot: 'bg-gray-400', container: 'bg-gray-50 border-gray-200' };
  }
}

export function TimeTrialAssessment({ data }: TimeTrialAssessmentProps) {
  const color = getZoneColor(data.zone);
  const classes = getZoneClasses(color);

  // "No Data" zone
  if (data.zone === 'No Data') {
    return (
      <div className={`rounded-md border p-3 ${classes.container}`}>
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${classes.dot}`} />
          <span className="text-sm font-medium text-gray-600">No Time Trial Data</span>
        </div>
      </div>
    );
  }

  // "Insufficient Group Data" zone
  if (data.zone === 'Insufficient Group Data') {
    return (
      <div className={`rounded-md border p-3 ${classes.container}`}>
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${classes.dot}`} />
          <span className="text-sm font-medium text-gray-600">Insufficient Group Data</span>
        </div>
      </div>
    );
  }

  // Normal zone display
  return (
    <div className={`rounded-md border p-3 ${classes.container}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${classes.dot}`} />
          <span className="text-sm font-semibold">{data.zone}</span>
        </div>
        <span className="text-xs text-gray-500">Time Trial</span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
        {data.rider_time != null && (
          <span>Rider: {formatTimeTrialTime(data.rider_time)}</span>
        )}
        {data.group_median != null && (
          <span>Median: {formatTimeTrialTime(data.group_median)}</span>
        )}
        {data.group_rider_count > 0 && (
          <span>vs {data.group_rider_count} riders in group</span>
        )}
      </div>

      {data.zone === 'Ahead of Group+' && data.cross_group && (
        <p className="mt-1 text-xs text-green-700">
          Faster than {data.cross_group.riders_beaten} of {data.cross_group.higher_group_rider_count} riders in {data.cross_group.higher_group_name}
        </p>
      )}
    </div>
  );
}
